import type { AccessSummary } from "@energyplatform/client";
import { session } from "./services.ts";

// What the signed-in person reaches (host: /me access). People with a tenant-wide role reach everything; people with
// grants on parts of the tree only the screens their grants allow. The host checks every request; this only keeps
// the application from offering what would be refused.

const EVERYTHING: AccessSummary = {
  unrestricted: true,
  nodes: [],
  capabilities: {},
};

export function access(): AccessSummary {
  const s = session();
  return s.status === "signedIn" ? (s.user.access ?? EVERYTHING) : EVERYTHING;
}

/** Whether the person reaches an area at a level somewhere in their part of the tree. */
export function can(capability: string, level = 1): boolean {
  const a = access();
  return a.unrestricted || (a.capabilities[capability] ?? 0) >= level;
}

/** The area a screen needs; screens about the whole organisation (integrations, devices) need a tenant-wide role. */
const SCREEN_NEEDS: Record<string, string | null> = {
  "/places": "measurements",
  "/overview": "measurements",
  "/flows": "measurements",
  "/analysis": "measurements",
  "/plans": "measurements",
  "/events": "measurements",
  "/assets": "measurements",
  "/commissioning": "structure",
  "/reports": "costs",
  "/finance": "costs",
  "/optimization": "costs",
  "/users": "users",
  "/devices": null,
  "/automations": null,
  "/cluster": "measurements",
  "/charging": null,
  "/integrations": null,
  "/connections": null,
  "/extensions": null,
  "/catalog": null,
};

/** Whether the navigation offers a screen to the person. */
export function offers(path: string): boolean {
  if (access().unrestricted) {
    return true;
  }
  const needs = SCREEN_NEEDS[path];
  return needs === undefined || (needs !== null && can(needs));
}
